import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  type Account,
  ApiError,
  type Contact,
  type Product,
  type Person,
  type Purchase,
  type SlabInput,
  type SlabReading,
  type Voucher,
  api,
  formatMoney, type SupplierDetails } from "./api";
import { singaporeToday } from "./dates";
import { slabLabelDescription } from "./slabLabel";
import { productName } from "./productName";
import { VoucherCorrection } from "./VoucherCorrection";
import { remainingVoucherCents, VoucherBalance } from "./VoucherBalance";
import { PageHeader } from "./layout/Layout";
import { DateField, SelectField } from "./fields";
import { type PickedFile, Attachments, cropToSlab, prepareFile } from "./attachments";
import { boxInCrop } from "./certCover";
import { fileForReading, ReadFileButton, unsureSentence, useReading } from "./reading";
import {
  Actions,
  Alert,
  Badge,
  Box,
  Empty,
  Field,
  IconAction,
  IconLink,
  Loading,
  Modal,
  Pager,
  StatCard,
  Table,
  Th,
  TwoLine,
  usePaged,
  Working,
} from "./ui";
import { productCode } from "./productCode";
import { priceForAmount, amountForPrice } from "./linePrice";
import { HoverPreview } from "./HoverPreview";
import { FlagButton, FlagNote, flaggedFirst } from "./FlagButton";
import { languageOf, PasteList, type PastedRow } from "./PasteList";
import { PRODUCT_TYPES, typeOf } from "./productTypes";
import type { ItemType } from "./api";
import { useBooks } from "./books";

/**
 * Purchases and payment vouchers (quotation A9, A11).
 *
 * The one rule that matters most here is shown on the form rather than written in a
 * manual: whoever paid owns what was bought. Picking the paying account says, in words,
 * whether this becomes company stock with a voucher or someone's own stock with none.
 */
/**
 * The part of a purchase that is stock bought to sell, and the part spent on the business
 * (D, 15 October 2026: one payment of 9,020, singles and booth rent, "make this neater").
 * A line of stock, or under a category in a group of stock bought to sell, is inventory;
 * any other category line is an expense. A purchase with both shows on both pages, each
 * with its own part.
 */
function partsOf(purchase: Purchase): { stockCents: number; expenseCents: number; hasStock: boolean; hasExpense: boolean } {
  const expense = purchase.lines.filter((line) => line.categoryId && !line.categoryForResale);
  const stock = purchase.lines.filter((line) => !line.categoryId || line.categoryForResale);
  return {
    stockCents: stock.reduce((sum, line) => sum + line.lineTotalCents, 0),
    expenseCents: expense.reduce((sum, line) => sum + line.lineTotalCents, 0),
    hasStock: stock.length > 0 || purchase.lines.length === 0,
    hasExpense: expense.length > 0,
  };
}

/** What this page shows of a purchase: all of it, or its part when it is mixed. */
function partCents(purchase: Purchase, expenses: boolean): number {
  const parts = partsOf(purchase);
  if (!(parts.hasStock && parts.hasExpense)) return purchase.totalCents;
  return expenses ? parts.expenseCents : parts.stockCents;
}

export function Purchases({
  mode,
  canVerify,
  canHandleVouchers,
}: {
  /**
   * Stock bought to sell, or what is bought for the business itself. The two are the same
   * kind of record and are kept on separate pages so they never mix (D, 11 October 2026).
   */
  readonly mode: "inventory" | "expenses";
  readonly canVerify: boolean;
  readonly canHandleVouchers: boolean;
}): React.ReactElement {
  const expenses = mode === "expenses";
  const [allPurchases, setPurchases] = useState<Purchase[]>([]);
  const [allVouchers, setVouchers] = useState<Voucher[]>([]);
  // Company purchases or what people paid for themselves, by the switch at the top (D, 12 October 2026).
  const books = useBooks();
  const onThisPage = (purchase: Purchase): boolean => (expenses ? partsOf(purchase).hasExpense : partsOf(purchase).hasStock);
  const purchases = allPurchases.filter((purchase) => onThisPage(purchase) && (purchase.ownerKind === "person") === (books === "personal"));
  const pageNumbers = new Set(allPurchases.filter(onThisPage).map((purchase) => purchase.number));
  // A voucher goes with its purchase, on each page it shows on; one with no purchase, a buyback's say, stays with stock.
  const vouchers = allVouchers.filter((voucher) => (voucher.purchaseNumber ? pageNumbers.has(voucher.purchaseNumber) : !expenses));
  const [correctingVoucher, setCorrectingVoucher] = useState<Voucher>();
  const [adding, setAdding] = useState(false);
  /** The purchase being keyed again, which replaces it on Save. */
  const [editing, setEditing] = useState<Purchase | undefined>();
  const [deleting, setDeleting] = useState<Purchase | undefined>();
  const [cancelling, setCancelling] = useState<Voucher | undefined>();
  /** The purchase whose invoice files are open. */
  const [filing, setFiling] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();

  async function reload(): Promise<void> {
    try {
      const [bought, paper] = await Promise.all([
        api.purchases(),
        canHandleVouchers ? api.vouchers() : Promise.resolve({ vouchers: [] as Voucher[] }),
      ]);
      setPurchases(bought.purchases);
      setVouchers(paper.vouchers);
      setError(undefined);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not load supplier purchases");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function verify(purchase: Purchase): Promise<void> {
    try {
      await api.verifyPurchase(purchase.id);
      await reload();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not mark the purchase as verified");
    }
  }

  async function verifyPaymentVoucher(voucher: Voucher): Promise<void> {
    try { await api.verifyVoucher(voucher.id); setNotice(`${voucher.number} verified. Verification does not mark it paid.`); await reload(); }
    catch (problem) { setError(problem instanceof Error ? problem.message : "Could not verify the voucher"); }
  }

  /**
   * A purchase deleted, or replaced by an edit, keeps its number on the books but is out of
   * the way here unless asked for (D, 6 October 2026: "what do I do with it?").
   */
  const [showDeleted, setShowDeleted] = useState(false);
  const deletedCount = purchases.filter((purchase) => purchase.status === "cancelled").length;
  /**
   * One search for both lists (D, 11 October 2026: "type 1556 and find it above and below"):
   * a number, a supplier or payee, an invoice number, or an amount with or without its cents.
   */
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase().replace(/^s?\$/, "");
  const found = (cents: number, ...words: (string | null | undefined)[]): boolean => {
    if (!term) return true;
    const amount = (cents / 100).toFixed(2);
    const typed = term.replace(/,/g, "");
    if (/^\d+(\.\d{0,2})?$/.test(typed) &&(amount === typed || amount.startsWith(typed.includes(".") ? typed : `${typed}.`))) return true;
    return words.some((word) => word?.toLowerCase().includes(term));
  };
  const shownPurchases = flaggedFirst((showDeleted ? purchases : purchases.filter((purchase) => purchase.status !== "cancelled")).filter((purchase) =>
    found(purchase.totalCents, purchase.number, purchase.supplierName, purchase.supplierInvoiceNo, purchase.voucherNumber, purchase.purchaseBatchRef),
  ));
  const pagedPurchases = usePaged(shownPurchases);
  const cancelledVouchers = vouchers.filter((voucher) => voucher.status === "cancelled").length;
  const shownVouchers = (showDeleted ? vouchers : vouchers.filter((voucher) => voucher.status !== "cancelled")).filter((voucher) =>
    found(voucher.amountCents, voucher.number, voucher.payeeName, voucher.description, voucher.purchaseNumber),
  );
  const pagedVouchers = usePaged(shownVouchers);

  if (loading) return <Loading />;

  const unpaid = vouchers.filter((voucher) => voucher.status === "unpaid");
  const vouchersByNumber = new Map(vouchers.map((voucher) => [voucher.number, voucher]));
  const unpaidCents = unpaid.reduce((sum, voucher) => sum + remainingVoucherCents(voucher), 0);
  const unchecked = purchases.filter((purchase) => !purchase.isVerified && purchase.status === "received").length;
  const thisMonth = singaporeToday().slice(0, 7);
  const monthCents = purchases
    .filter((purchase) => purchase.status === "received" && purchase.purchasedOn.startsWith(thisMonth))
    .reduce((sum, purchase) => sum + partCents(purchase, expenses), 0);

  return (
    <>
      <PageHeader
        title={expenses ? "Expenses" : books === "personal" ? "Inventory purchases - personal books" : "Inventory purchases"}
        subtitle={
          books === "personal" && !expenses
            ? "Stock that people bought with their own money. No payment voucher is raised and nothing goes through the bank; Staff balances shows what the company owes each of them."
            : 
          expenses
            ? "What is bought for the business itself: fittings, renovation, software and the like, with their payment vouchers. Nothing here goes into inventory."
            : "Stock bought for resale and its payment vouchers. Inventory ownership follows whoever paid."
        }
        actions={
          <button type="button" className="ti-btn ti-btn-primary-full" onClick={() => setAdding(true)}>
            <i className="ri-add-line" /> {expenses ? "Record expense" : "Record inventory purchase"}
          </button>
        }
      />

      {error ? <Alert tone="danger" onClose={() => setError(undefined)}>{error}</Alert> : null}
      {notice ? <Alert tone="success" onClose={() => setNotice(undefined)}>{notice}</Alert> : null}

      <div className="grid grid-cols-12 gap-x-6">
        <div className={`${canHandleVouchers ? "xl:col-span-4" : "xl:col-span-6"} md:col-span-6 col-span-12`}>
          <StatCard
            label={expenses ? "Expenses this month" : "Purchases this month"}
            value={formatMoney(monthCents)}
            icon="ti ti-shopping-cart"
            tone="primary"
            note="Company and personal purchases combined"
          />
        </div>
        <div className={`${canHandleVouchers ? "xl:col-span-4" : "xl:col-span-6"} md:col-span-6 col-span-12`}>
          <StatCard
            label={expenses ? "Expenses awaiting verification" : "Purchases awaiting verification"}
            value={unchecked}
            icon="ti ti-list-check"
            tone={unchecked ? "warning" : "success"}
            note={unchecked ? "Verify each purchase against its invoice" : "All purchases verified"}
          />
        </div>
        {canHandleVouchers ? (
          <div className="xl:col-span-4 md:col-span-6 col-span-12">
            <StatCard
              label="Unpaid payment vouchers"
              value={formatMoney(unpaidCents)}
              icon="ti ti-file-invoice"
              tone={unpaid.length ? "danger" : "success"}
              note={unpaid.length ? `${unpaid.length} awaiting payment` : "No outstanding voucher payments"}
              {...(unpaid.length ? { noteTone: "danger" as const } : {})}
            />
          </div>
        ) : null}
      </div>

      <div className="input-group mb-4">
        <span className="input-group-text">
          <i className="ri-search-line" />
        </span>
        <input
          id="purchase-search"
          type="text"
          className="form-control"
          value={search}
          placeholder={expenses ? "Search expenses and vouchers by amount, number, payee or invoice number" : "Search purchases and vouchers by amount, number, supplier or invoice number"}
          aria-label="Search purchases and vouchers"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <Box
        title={expenses ? "Expenses" : "Inventory purchases"}
        count={shownPurchases.length}
        actions={
          deletedCount > 0 ? (
            <button type="button" className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0" onClick={() => setShowDeleted(!showDeleted)}>
              {showDeleted ? "Hide deleted" : `Show deleted (${deletedCount})`}
            </button>
          ) : undefined
        }
      >
        {shownPurchases.length === 0 ? (
          <Empty icon="ri-shopping-cart-line">{term ? "Nothing matches the search." : expenses ? "No expenses have been recorded." : "No inventory purchases have been recorded."}</Empty>
        ) : (
          <Table
            compact
            head={
              <>
                <Th>Date</Th>
                <Th>{expenses ? "Paid to" : "Supplier"}</Th>
                <Th end>Total</Th>
                <Th>Number and voucher</Th>
                <Th>Paid from</Th>
                <Th>Status</Th>
                <Th end>Action</Th>
              </>
            }
          >
            {pagedPurchases.rows.map((purchase) => (
              <tr key={purchase.id} className={`border-b border-defaultborder ${purchase.flagged ? "bg-danger/[0.06]" : ""}`}>
                <td>
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <FlagButton kind="purchase" id={purchase.id} flagged={Boolean(purchase.flagged)} note={purchase.flagNote} onChanged={() => void reload()} />
                    {purchase.purchasedOn}
                  </div>
                  {purchase.flagged ? <FlagNote note={purchase.flagNote} /> : null}
                </td>
                <td>
                  <TwoLine
                    main={purchase.supplierName}
                    sub={
                      [purchase.supplierInvoiceNo && `Invoice ${purchase.supplierInvoiceNo}`, purchase.purchaseBatchRef]
                        .filter(Boolean)
                        .join(" · ") || undefined
                    }
                  />
                </td>
                <td className="!text-end font-semibold">{formatMoney(partCents(purchase, expenses))}
                  {partCents(purchase, expenses) !== purchase.totalCents ? (
                    <div className="font-normal text-[0.75rem] text-[#8c9097] dark:text-white/50">
                      of {formatMoney(purchase.totalCents)}, the rest on {expenses ? "Inventory purchases" : "Expenses"}
                    </div>
                  ) : null}
                  {purchase.status === "received" && purchase.voucherNumber && vouchersByNumber.has(purchase.voucherNumber) ? <div className="text-start font-normal"><VoucherBalance voucher={vouchersByNumber.get(purchase.voucherNumber)!} /></div> : null}
                </td>
                <td className="whitespace-nowrap">
                  <span className="block font-semibold">{purchase.number}</span>
                  <span className="block text-[0.75rem] text-[#8c9097] dark:text-white/50">{purchase.voucherNumber ?? "No voucher"}</span>
                </td>
                <td>
                  <span className="block">{purchase.paidFromAccountName}</span>
                  <Badge tone={purchase.ownerKind === "company" ? "primary" : "secondary"}>{purchase.ownerName}</Badge>
                </td>
                <td>
                  {purchase.status === "cancelled" ? (
                    <Badge tone="light">{purchase.cancelReason === "edited" ? "Replaced by edit" : "Deleted"}</Badge>
                  ) : purchase.isVerified ? (
                    <Badge tone="success">Verified</Badge>
                  ) : (
                    <Badge tone="warning">Awaiting verification</Badge>
                  )}
                </td>
                <td>
                  <Actions>
                    {/* The supplier's invoice shown while the pointer rests here (D, 8 October 2026). */}
                    <HoverPreview
                      src={purchase.attachments[0] ? `/api/photos/${purchase.attachments[0].id}?preview=1` : null}
                      kind={purchase.attachments[0]?.contentType === "application/pdf" ? "pdf" : "image"}
                      content={<KeyedItems purchase={purchase} />}
                    >
                      <IconAction
                        icon="ri-attachment-2"
                        label={
                          purchase.attachments.length
                            ? `Supplier invoice (${purchase.attachments.length} ${purchase.attachments.length === 1 ? "file" : "files"})`
                            : "Attach the supplier invoice"
                        }
                        tone={purchase.attachments.length ? "primary" : "secondary"}
                        onClick={() => setFiling(purchase.id)}
                      />
                    </HoverPreview>
                    {purchase.status === "received" ? (
                      <>
                        <IconAction icon="ri-edit-line" label="Edit" tone="primary" onClick={() => setEditing(purchase)} />
                        <IconAction icon="ri-delete-bin-line" label="Delete" tone="danger" onClick={() => setDeleting(purchase)} />
                      </>
                    ) : null}
                    {canVerify && purchase.status === "received" && !purchase.isVerified ? (
                      <IconAction
                        icon="ri-check-double-line"
                        label="Mark checked against the invoice"
                        tone="success"
                        onClick={() => void verify(purchase)}
                      />
                    ) : null}
                  </Actions>
                </td>
              </tr>
            ))}
          </Table>
        )}
        <Pager
          total={shownPurchases.length}
          page={pagedPurchases.page}
          pages={pagedPurchases.pages}
          onPage={pagedPurchases.setPage}
        />
      </Box>

      {canHandleVouchers ? (
        <Box
          title="Payment vouchers"
          count={shownVouchers.length}
          actions={
            unpaid.length || cancelledVouchers ? (
              <div className="flex items-center gap-2">
                {unpaid.length ? (
                  <Badge tone="danger">
                    {unpaid.length} unpaid, {formatMoney(unpaidCents)}
                  </Badge>
                ) : null}
                {/* Cancelled ones keep their numbers but stay out of the way, as deleted purchases do. */}
                {cancelledVouchers ? (
                  <button
                    type="button"
                    className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0"
                    onClick={() => setShowDeleted(!showDeleted)}
                  >
                    {showDeleted ? "Hide cancelled" : `Show cancelled (${cancelledVouchers})`}
                  </button>
                ) : null}
              </div>
            ) : undefined
          }
        >
          <p className="text-[0.813rem] text-[#8c9097] dark:text-white/50 mb-4">
            Verify the purchase against its invoice, then Verify voucher. Verification does not mark it paid.
            Match the actual bank payment on Bank reconciliation. Cash payments are recorded here.
          </p>
          {shownVouchers.length === 0 ? (
            <Empty icon="ri-file-list-3-line">No payment vouchers have been issued.</Empty>
          ) : (
            <Table
              compact
              head={
                <>
                  <Th>Date</Th>
                  <Th>Payee</Th>
                  <Th end>Amount</Th>
                  <Th>Number</Th>
                  <Th>Status</Th>
                  <Th end>Action</Th>
                </>
              }
            >
              {pagedVouchers.rows.map((voucher) => (
                <tr key={voucher.id} className={`border-b border-defaultborder ${voucher.status === "unpaid" ? "bg-warning/5" : ""}`}>
                  <td className="whitespace-nowrap">{voucher.issuedOn}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      {/* The cards a buyback paid for, larger on hover (D, 11 October 2026). */}
                      {voucher.photoIds?.[0] ? (
                        <HoverPreview src={`/api/photos/${voucher.photoIds[0]}?preview=1`} kind="image">
                          <a href={`/api/photos/${voucher.photoIds[0]}`} target="_blank" rel="noreferrer" className="shrink-0">
                            <img src={`/api/photos/${voucher.photoIds[0]}?preview=1`} alt="" loading="lazy" className="w-9 h-9 rounded-sm object-cover border border-defaultborder" />
                          </a>
                        </HoverPreview>
                      ) : null}
                      <TwoLine main={voucher.payeeName} sub={voucher.description} />
                    </div>
                  </td>
                  <td className="!text-end font-semibold">{formatMoney(voucher.amountCents)}</td>
                  <td className="font-semibold whitespace-nowrap">{voucher.number}</td>
                  <td>
                    {voucher.status === "paid" ? (
                      <Badge tone="success">Paid {voucher.paidOn}</Badge>
                    ) : voucher.status === "unpaid" && voucher.partPaidCents ? (
                      <Badge tone="info">Part paid {formatMoney(voucher.partPaidCents)}</Badge>
                    ) : voucher.status === "unpaid" ? (
                      <Badge tone="warning">Unpaid</Badge>
                    ) : (
                      <Badge tone="light">Cancelled</Badge>
                    )}
                    <VoucherBalance voucher={voucher} />
                    {voucher.status !== "cancelled" ? <div className="mt-1"><Badge tone={voucher.isVerified ? "success" : "light"}>{voucher.isVerified ? "Voucher verified" : "Voucher not verified"}</Badge></div> : null}
                    {voucher.status === "paid" && voucher.paidFromAccountKind === "bank" && !voucher.hasBankMatches ? <p className="text-warning text-[0.75rem] mt-1 mb-0">Marked paid without a bank match. Review payment before matching.</p> : null}
                  </td>
                  <td>
                    <Actions>
                      {/* The voucher shown while the pointer rests here; only a click prints it. */}
                      <HoverPreview src={`/api/vouchers/${voucher.id}/voucher.pdf?preview=1`} kind="pdf">
                        <IconLink
                          icon="ri-printer-line"
                          label="Print the voucher"
                          tone="primary"
                          href={`/api/vouchers/${voucher.id}/voucher.pdf`}
                        />
                      </HoverPreview>
                      {voucher.status !== "cancelled" ? <IconAction icon="ri-pencil-line" label="Edit voucher dates or payment" tip="Edit dates or undo payment" tone="info" onClick={() => setCorrectingVoucher(voucher)} /> : null}
                      {canVerify && voucher.status !== "cancelled" && !voucher.isVerified ? <IconAction icon="ri-check-double-line" label="Verify voucher" tip="Check the voucher; does not mark paid" tone="success" onClick={() => void verifyPaymentVoucher(voucher)} /> : null}
                      {voucher.status === "unpaid" ? (
                        <>
                          <IconAction
                            icon="ri-checkbox-circle-line"
                            label={voucher.paidFromAccountKind === "bank" ? "Review bank payment" : "Record payment"}
                            tip={voucher.paidFromAccountKind === "bank" ? "Bank payments are matched on Bank reconciliation" : "Record voucher payment"}
                            tone="success"
                            onClick={() => setCorrectingVoucher(voucher)}
                          />
                          <IconAction
                            icon={voucher.purchaseNumber ? "ri-delete-bin-line" : "ri-close-circle-line"}
                            label={voucher.purchaseNumber ? "Delete voucher" : "Cancel voucher"}
                            tone="danger"
                            onClick={() => setCancelling(voucher)}
                          />
                        </>
                      ) : null}
                      {/* Paid and not for a purchase: a buyback made up to try the booth, say. A purchase's goes with the purchase. */}
                      {voucher.status === "paid" ? (
                        <IconAction
                          icon="ri-delete-bin-line"
                          label="Delete voucher"
                          tone="danger"
                          onClick={() => setCancelling(voucher)}
                        />
                      ) : null}
                    </Actions>
                  </td>
                </tr>
              ))}
            </Table>
          )}
          <Pager
            total={shownVouchers.length}
            page={pagedVouchers.page}
            pages={pagedVouchers.pages}
            onPage={pagedVouchers.setPage}
          />
        </Box>
      ) : null}

      {correctingVoucher ? <VoucherCorrection voucher={correctingVoucher} purchase={purchases.find((purchase) => purchase.number === correctingVoucher.purchaseNumber)} onClose={() => setCorrectingVoucher(undefined)} onDone={() => { setCorrectingVoucher(undefined); void reload(); }} /> : null}
      {editing ? (
        <NewPurchase
          // A purchase with stock on it is edited where stock can be keyed, wherever it was opened from.
          mode={editing.lines.some((line) => !line.categoryId) ? "inventory" : mode}
          editing={editing}
          previousPurchases={purchases}
          onDraft={(kept) => {
            setEditing(undefined);
            setNotice(
              `Draft saved. Open "${expenses ? "Record expense" : "Record inventory purchase"}" to carry on with it.` +
                (kept === "all" ? "" : " The invoice file was too large to keep with the draft: attach it again when you come back."),
            );
          }}
          onClose={() => setEditing(undefined)}
          onSaved={(purchase) => {
            setEditing(undefined);
            setNotice(`${editing.number} is replaced by ${purchase.number}.`);
            void reload();
          }}
        />
      ) : null}

      {deleting ? (
        <DeletePurchase
          purchase={deleting}
          onClose={() => setDeleting(undefined)}
          onDone={() => {
            setNotice(`${deleting.number} deleted. Its inventory is taken out and its payment voucher cancelled.`);
            setDeleting(undefined);
            void reload();
          }}
        />
      ) : null}

      {adding ? (
        <NewPurchase
          mode={mode}
          previousPurchases={purchases}
          onDraft={(kept) => {
            setAdding(false);
            setNotice(
              `Draft saved. Open "${expenses ? "Record expense" : "Record inventory purchase"}" to carry on with it.` +
                (kept === "all" ? "" : " The invoice file was too large to keep with the draft: attach it again when you come back."),
            );
          }}
          onClose={() => setAdding(false)}
          onSaved={(purchase) => {
            setAdding(false);
            setNotice(
              purchase.voucherNumber
                ? `${purchase.number} saved. Payment voucher ${purchase.voucherNumber} issued for ${formatMoney(purchase.totalCents)}.`
                : `${purchase.number} saved as inventory personally funded by ${purchase.ownerName}. No payment voucher was issued because the company did not pay.`,
            );
            void reload();
          }}
        />
      ) : null}

      {filing ? (
        <Modal open title={`Supplier invoice - ${purchases.find((entry) => entry.id === filing)?.number ?? ""}`} onClose={() => setFiling(undefined)}>
          <Attachments
            id="purchase-attach"
            items={purchases.find((entry) => entry.id === filing)?.attachments ?? []}
            onAdd={async (file) => {
              await api.attachToPurchase(filing, file);
              await reload();
            }}
            onRemove={async (attachment) => {
              await api.removePurchaseAttachment(filing, attachment.id);
              await reload();
            }}
          />
        </Modal>
      ) : null}

      {cancelling ? (
        <CancelVoucher
          voucher={cancelling}
          onClose={() => setCancelling(undefined)}
          onDone={() => {
            setNotice(
              cancelling.status === "paid"
                ? `${cancelling.number} deleted, with what it paid for. The voucher number is retained.`
                : `${cancelling.number} cancelled. The voucher number is retained.`,
            );
            setCancelling(undefined);
            void reload();
          }}
        />
      ) : null}
    </>
  );
}

function DeletePurchase({
  purchase,
  onClose,
  onDone,
}: {
  readonly purchase: Purchase;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      await api.cancelPurchase(purchase.id, reason);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not delete the purchase");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`Delete ${purchase.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="ti-btn ti-btn-danger-full" disabled={busy || !reason.trim()} onClick={() => void submit()}>
            {busy ? <Working label="Deleting" /> : "Delete purchase"}
          </button>
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-[0.813rem] mb-4">
          {formatMoney(purchase.totalCents)} from {purchase.supplierName}. Its inventory is taken out and its payment
          voucher cancelled. The purchase number is kept, marked deleted.
        </p>
        <Field id="delete-reason" label="Reason">
          <input
            id="delete-reason"
            type="text"
            className="form-control"
            value={reason}
            placeholder="Keyed wrong"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}

function CancelVoucher({
  voucher,
  onClose,
  onDone,
}: {
  readonly voucher: Voucher;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError(undefined);
    try {
      // A paid one is deleted with what it paid for: the cash back in the drawer, the buyback called off.
      if (voucher.status === "paid" || voucher.purchaseNumber) await api.deleteVoucher(voucher.id, reason);
      else await api.cancelVoucher(voucher.id, reason);
      onDone();
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not cancel the voucher");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`${voucher.status === "paid" || voucher.purchaseNumber ? "Delete" : "Cancel"} voucher ${voucher.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Keep voucher
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-danger-full"
            disabled={busy || !reason.trim()}
            onClick={() => void submit()}
          >
            {busy ? <Working label="Cancelling" /> : voucher.status === "paid" || voucher.purchaseNumber ? "Delete voucher" : "Cancel voucher"}
          </button>
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <p className="text-[0.813rem] mb-4">
          {formatMoney(voucher.amountCents)} to {voucher.payeeName}.{" "}
          {voucher.purchaseNumber ? `This also deletes purchase ${voucher.purchaseNumber} and takes its inventory out. Bank matches and recorded payments must be undone first, and stock already sold or moved prevents deletion. ` : voucher.status === "paid"
            ? "The money is put back on the books, cash into the drawer, and a buyback it paid for is called off with any of its cards in stock. "
            : ""}
          A cancelled voucher retains its number, so
          voucher numbering has no gaps.
        </p>
        <Field id="cancel-reason" label="Reason for cancellation">
          <input
            id="cancel-reason"
            type="text"
            className="form-control"
            value={reason}
            placeholder="Entered twice by mistake"
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>
      </form>
    </Modal>
  );
}

interface DraftLine {
  readonly key: number;
  productId: string;
  isSlab: boolean;
  description: string;
  quantity: string;
  lineTotal: string;
  unitPrice: string;
  /** What the invoice said on this line, when no product matched it and a person has to pick. */
  readAs?: string;
  /** A graded card's label, read from its photo: cert, grader and grade go onto the slab. */
  slab?: SlabInput;
  /** That photo, kept with the slab once the purchase is saved. */
  slabPhoto?: PickedFile;
  /** Not stock for resale: the category the money went to (D, 11 October 2026). */
  categoryId?: string;
  /**
   * On an inventory purchase, a line that is not stock: the trolley bought along with the
   * boxes (D, 12 October 2026: "it has 4 categories, but they put it in 1 large
   * transaction"). It picks a category where the other lines pick a product.
   */
  notForResale?: boolean;
  /**
   * A product not on the list yet, made when the purchase is saved (D, 14 October 2026:
   * singles, slabs and sealed products paid in one go). The description is its name.
   */
  newProduct?: { itemType: ItemType };
}

/** The shop's tab for a new product of this type. */
const categoryFor = (itemType: ItemType): string => (itemType === "single" ? "Singles" : itemType === "accessory" ? "Accessories" : "Sealed Products");

const SLAB_WORDS: Readonly<Record<string, string>> = {
  certNo: "the cert number",
  grader: "the grader",
  grade: "the grade",
  cardName: "the card name",
  setName: "the set",
  cardNumber: "the card number",
};

const INVOICE_WORDS: Readonly<Record<string, string>> = {
  supplierName: "the supplier's name",
  invoiceNo: "the invoice number",
  invoiceDate: "the date",
  currency: "the currency",
  total: "the total",
  lines: "the line items",
};

let nextKey = 1;
function blankLine(): DraftLine {
  return { key: nextKey++, productId: "", isSlab: false, description: "", quantity: "1", lineTotal: "", unitPrice: "" };
}

/**
 * The slab line a label belongs to: the one whose name carries the label's card number
 * ("Charizard V #307 PSA 10" for card 307), or shares the most words with the card's name.
 * Nothing when no line fits well enough, so a photo is never put on the wrong card's price.
 */
export function slabLineFor<T extends { readonly description: string; readonly slab?: SlabInput | undefined }>(
  open: readonly T[],
  got: { readonly cardNumber: string | null; readonly cardName: string | null; readonly grade: string | null },
): T | undefined {
  const number = (got.cardNumber ?? "").match(/[A-Z]*\d+/i)?.[0]?.replace(/^([A-Z]*)0+(?=\d)/i, "$1").toLowerCase();
  const words = (got.cardName ?? "").toLowerCase().split(/[^a-z0-9]+/).filter((word) => word.length >= 3);
  let best: T | undefined;
  let bestScore = 0;
  for (const line of open) {
    const text = `${line.description} ${line.slab?.cardName ?? ""}`.toLowerCase();
    const numbers = [...text.matchAll(/#\s*([a-z]*)0*(\d+)/g)].map((found) => `${found[1]}${found[2]}`);
    let score = number && numbers.includes(number) ? 3 : 0;
    score += words.filter((word) => text.includes(word)).length;
    const grade = /\b(?:psa|bgs|cgc|sgc|tag|ace)\s*(\d+(?:\.\d)?)\b/.exec(text)?.[1];
    if (grade && got.grade && grade === got.grade.trim()) score += 0.5;
    if (score > bestScore) {
      best = line;
      bestScore = score;
    }
  }
  return bestScore >= 2 ? best : undefined;
}

/** What was keyed on a purchase, for the hover when there is no invoice to show. */
function KeyedItems({ purchase }: { readonly purchase: Purchase }): React.ReactElement {
  return (
    <>
      <span className="block font-semibold mb-1">
        {purchase.number} · {purchase.purchasedOn} · {purchase.supplierName}
      </span>
      <span className="block text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-2">No invoice attached. The items as keyed:</span>
      <table className="w-full text-[0.8125rem]">
        <thead>
          <tr className="border-b border-defaultborder text-[#8c9097] dark:text-white/50">
            <th className="text-start py-1 font-medium">Item</th>
            <th className="text-end px-2 py-1 font-medium">Qty</th>
            <th className="text-end px-2 py-1 font-medium whitespace-nowrap">Price S$</th>
            <th className="text-end ps-2 py-1 font-medium whitespace-nowrap">Amount S$</th>
          </tr>
        </thead>
        <tbody>
          {purchase.lines.map((line) => (
            <tr key={line.lineNo} className="border-b border-defaultborder">
              <td className="py-1 pe-2 break-words">
                {line.description}
                {line.categoryName ? <span className="block text-[0.6875rem] text-[#8c9097] dark:text-white/50">{line.categoryName}</span> : null}
              </td>
              <td className="text-end px-2 py-1 tabular-nums align-top">{line.quantity}</td>
              <td className="text-end px-2 py-1 tabular-nums align-top">{formatMoney(line.quantity ? Math.round(line.lineTotalCents / line.quantity) : line.lineTotalCents)}</td>
              <td className="text-end ps-2 py-1 tabular-nums align-top">{formatMoney(line.lineTotalCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <span className="block text-end font-semibold mt-2">Total S$ {formatMoney(purchase.totalCents)}</span>
    </>
  );
}

/** Reads a typed amount without trusting it. Returns cents, or undefined if unreadable. */
function toCents(text: string): number | undefined {
  const cleaned = text.trim().replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return undefined;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

/** A new purchase, laid out like the template's create invoice page: details, then lines. */
function NewPurchase({
  mode,
  editing,
  previousPurchases,
  onClose,
  onSaved,
  onDraft,
}: {
  readonly mode: "inventory" | "expenses";
  /** A purchase to key again: the form starts from it, and Save replaces it. */
  readonly editing?: Purchase | undefined;
  readonly previousPurchases: readonly Purchase[];
  readonly onClose: () => void;
  readonly onSaved: (purchase: Purchase) => void;
  /** The form was put down as a draft; says whether the invoice file could be kept with it. */
  readonly onDraft: (kept: "all" | "without the file") => void;
}): React.ReactElement {
  const expenses = mode === "expenses";
  const books = useBooks();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [suppliers, setSuppliers] = useState<Contact[]>([]);
  const [supplierPeople, setSupplierPeople] = useState<Person[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  /** Money-out categories, for a line that is not stock for resale: lighting, software. */
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    void api
      .categories()
      .then((result) => setCategories(result.categories.filter((one) => one.isActive && one.direction === "money_out")))
      .catch(() => undefined);
  }, []);
  /**
   * A form put down half done and picked up again (D, 12 October 2026: "save as draft,
   * refresh the page, then afterwards I come back", to add a product that is not on the list
   * yet). Kept in this browser, one for purchases and one for expenses, until it is saved
   * or thrown away.
   */
  const [draft] = useState<PurchaseDraft | undefined>(() => (editing ? undefined : readDraft(mode)));
  const [fromDraft, setFromDraft] = useState(draft !== undefined);
  const [purchasedOn, setPurchasedOn] = useState(editing?.purchasedOn ?? draft?.purchasedOn ?? singaporeToday());
  const [supplierId, setSupplierId] = useState(editing?.supplierPersonId ? `person:${editing.supplierPersonId}` : editing?.supplierContactId ?? draft?.supplierId ?? "");
  const [supplierName, setSupplierName] = useState(editing && !editing.supplierContactId ? editing.supplierName : (draft?.supplierName ?? ""));
  const [accountId, setAccountId] = useState(editing?.paidFromAccountId ?? draft?.accountId ?? "");
  const [batchRef, setBatchRef] = useState(editing?.purchaseBatchRef ?? draft?.batchRef ?? "");
  const [invoiceNo, setInvoiceNo] = useState(editing?.supplierInvoiceNo ?? draft?.invoiceNo ?? "");
  /**
   * A discount off the whole invoice (D, 10 October 2026: "there is a $5 payment discount
   * here, how do I match it?"). It is spread over the lines on saving, so the purchase comes
   * to what was paid and each product's cost is what it really cost.
   */
  const [discount, setDiscount] = useState(draft?.discount ?? "");
  /** The supplier's address, phone and so on, read off the invoice and kept with the supplier. */
  const [supplierDetails, setSupplierDetails] = useState<SupplierDetails | undefined>(draft?.supplierDetails);
  const [lines, setLines] = useState<DraftLine[]>(
    editing
      ? editing.lines.map((line) => ({
          ...blankLine(),
          productId: line.productId ?? "",
          isSlab: line.isSlab,
          ...(line.slab ? { slab: line.slab } : {}),
          ...(line.categoryId ? { categoryId: line.categoryId, notForResale: true } : {}),
          description: line.isSlab || line.categoryId ? line.description : "",
          quantity: String(line.quantity),
          lineTotal: (line.lineTotalCents / 100).toFixed(2),
          unitPrice: priceForAmount((line.lineTotalCents / 100).toFixed(2), String(line.quantity)),
        }))
      : draft?.lines.length
        ? draft.lines.map((line) => ({ ...line, key: blankLine().key }))
        : [blankLine()],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const canRead = useReading();
  const [reading, setReading] = useState(false);
  const [readNotes, setReadNotes] = useState<string[]>([]);
  /** The supplier's invoice, saved with the purchase. */
  const [invoiceFile, setInvoiceFile] = useState<{ name: string; file: PickedFile } | undefined>(draft?.invoiceFile);

  /**
   * Read a supplier's invoice, a photo or a PDF, into the form. Each line is matched to a
   * product by name where one plainly matches; the rest are left for a person to pick,
   * with the invoice's own words underneath so they know what they are looking for.
   */
  async function readInvoiceFile(file: File): Promise<void> {
    setReading(true);
    setError(undefined);
    setReadNotes([]);
    try {
      const picked = await fileForReading(file);
      // The invoice read into the form is the one kept with the purchase.
      if (!invoiceFile) setInvoiceFile({ name: file.name, file: await prepareFile(file) });
      const { reading: got } = await api.readInvoice(picked);
      if (got.supplierName && !supplierId && !supplierName) setSupplierName(got.supplierName);
      if (got.invoiceNo && !invoiceNo) setInvoiceNo(got.invoiceNo);
      if (got.supplier && Object.values(got.supplier).some(Boolean)) setSupplierDetails(got.supplier);
      if (got.discount && !discount) setDiscount(got.discount);
      if (got.invoiceDate) setPurchasedOn(got.invoiceDate);

      // A product the invoice line plainly names: its code as a word of its own, or its full
      // name, the longest that fits. Not a few letters inside a card's name (D, 28 October
      // 2026: singles on a DenryuTCG invoice were put against unrelated products), and not a
      // name that only says what kind of thing it is, such as Singles.
      const known = (words: string): Product | undefined => {
        const wanted = ` ${words.toLowerCase().replace(/[^a-z0-9#/&']+/g, " ")} `;
        const whole = (text: string): boolean => {
          const tidy = text.toLowerCase().replace(/[^a-z0-9#/&']+/g, " ").trim();
          return tidy.length >= 4 && wanted.includes(` ${tidy} `);
        };
        const generic = /^(singles?|slabs?|unlisted item|lucky pull|gatchapon.*)$/i;
        return products
          .filter((product) => !generic.test(product.name.trim()) && (whole(product.code) || whole(productName(product)) || whole(product.name)))
          .sort((a, b) => b.name.length - a.name.length)[0];
      };
      if (got.lines.length > 0) {
        setLines(
          got.lines.map((line) => {
            // Picked for these words on an earlier invoice, or plainly named in them.
            const product = products.find((entry) => entry.id === line.productId) ?? known(line.description);
            const drafted = blankLine();
            if (expenses) {
              drafted.categoryId = "";
              drafted.description = line.description;
              drafted.quantity = String(line.quantity);
              drafted.lineTotal = line.lineTotal ?? "";
              drafted.unitPrice = line.unitPrice ?? priceForAmount(drafted.lineTotal, drafted.quantity);
              return drafted;
            }
            drafted.productId = product?.id ?? "";
            drafted.quantity = String(line.quantity);
            drafted.lineTotal = line.lineTotal ?? "";
            drafted.unitPrice = line.unitPrice ?? priceForAmount(drafted.lineTotal, drafted.quantity);
            drafted.readAs = line.description;
            return drafted;
          }),
        );
      }
      const notes = [...got.warnings];
      const unsure = unsureSentence(got.unsure, INVOICE_WORDS);
      if (unsure) notes.push(unsure);
      const unmatched = got.lines.filter((line) => !line.productId && !known(line.description)).length;
      if (unmatched > 0 && !expenses) notes.push(`${unmatched} ${unmatched === 1 ? "line does" : "lines do"} not match a product in the catalogue. Select the product, or add it to the catalogue first.`);
      setReadNotes(notes.length ? notes : ["Details read from the invoice. Review them, then save."]);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not read the invoice");
    } finally {
      setReading(false);
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const [a, c, p, people] = await Promise.all([api.accounts(), api.contacts(), api.products(), api.people()]);
        setSupplierPeople(people.people);
        const active = a.accounts.filter((account) => account.isActive);
        setAccounts(active);
        // D, 7 October 2026: someone already added as a customer can also sell to OHC.
        setSuppliers(c.contacts);
        setProducts(p.products.filter((product) => product.isActive));
        // On the personal books it starts on a person's own account, not the company's.
        const company = books === "personal" ? active.find((account) => account.ownerKind === "person") : active.find((account) => account.ownerKind === "company" && account.kind === "bank");
        if (company && !editing && !draft?.accountId) setAccountId(company.id);
      } catch (problem) {
        setError(problem instanceof ApiError ? problem.message : "Could not load the lists");
      }
    })();
  }, []);

  const account = accounts.find((entry) => entry.id === accountId);
  const historicalSuppliers = [...new Set(previousPurchases.filter((purchase) => purchase.status === "received").map((purchase) => purchase.supplierName.trim()).filter(Boolean))]
    .filter((name) => !suppliers.some((supplier) => supplier.name.toLowerCase() === name.toLowerCase()) && !supplierPeople.some((person) => person.fullName.toLowerCase() === name.toLowerCase()))
    .sort((a, b) => a.localeCompare(b));
  const historicalSupplier = historicalSuppliers.find((name) => supplierId === `history:${encodeURIComponent(name)}`);
  const gross = useMemo(() => lines.reduce((sum, line) => sum + (toCents(line.lineTotal) ?? 0), 0), [lines]);
  const discountCents = Math.min(gross, toCents(discount) ?? 0);
  const total = gross - discountCents;

  /** Which line's label is being read, if any. */
  const [readingLine, setReadingLine] = useState<number | undefined>();

  /** The label of a graded card on one line, read from its photo, into that line. */
  /** What a label read off a photo puts on a slab line. */
  async function slabFromPhoto(file: File): Promise<{ got: SlabReading; change: Partial<DraftLine> }> {
    const picked = await fileForReading(file);
    const { reading: got } = await api.readSlab({ contentType: picked.contentType, data: picked.data });
    const slabPhoto = await cropToSlab(file, got.slabBox).catch(() => undefined);
    const name = slabLabelDescription(got);
    return {
      got,
      change: {
        ...(name ? { description: name } : {}),
        ...(slabPhoto ? { slabPhoto } : {}),
        slab: {
          certNo: got.certNo,
          grader: got.grader,
          grade: got.grade,
          gradeDescription: got.gradeDescription,
          cardName: got.cardName,
          setName: got.setName,
          yearReleased: got.yearReleased,
          cardNumber: got.cardNumber,
          language: got.language,
          rarity: got.rarity,
          era: got.yearRegionEra ?? null,
          labelText: got.labelText ?? null,
          certBox: slabPhoto ? boxInCrop(got.slabBox, got.certBox) : null,
          // Covered unless somebody takes it off (D, 10 October 2026).
          hideCert: Boolean(slabPhoto && boxInCrop(got.slabBox, got.certBox)),
        },
      },
    };
  }

  const linesNow = useRef(lines);
  linesNow.current = lines;
  const [readingMany, setReadingMany] = useState<{ at: number; of: number } | undefined>();

  /**
   * Many slab photos read in one go (D, 14 October 2026: "can I drag like, lets say 20 slabs
   * photos in and then check straight away"). Each label fills the slab line it belongs to,
   * found by its card number and name among the slab lines with no cert yet, keeping the
   * line's own name and price. A label that fits no line is added as a new slab line.
   * Two are read at a time.
   */
  async function readSlabPhotos(files: readonly File[]): Promise<void> {
    const photos = files.filter((file) => file.type.startsWith("image/") || /\.(jpe?g|png|heic|webp)$/i.test(file.name));
    if (photos.length === 0) return;
    setError(undefined);
    setReadNotes([]);
    const claimed = new Set<number>();
    const failed: string[] = [];
    const unsure: string[] = [];
    let filled = 0;
    let added = 0;
    let done = 0;
    setReadingMany({ at: 0, of: photos.length });
    const queue = [...photos];
    const worker = async (): Promise<void> => {
      for (let file = queue.shift(); file; file = queue.shift()) {
        try {
          const { got, change } = await slabFromPhoto(file);
          const open = linesNow.current.filter((line) => line.isSlab && !line.slab?.certNo?.trim() && !claimed.has(line.key));
          const target = slabLineFor(open, got);
          if (target) {
            claimed.add(target.key);
            filled++;
            // The line keeps the name it was given; the label's details go onto the slab.
            const { description: _label, ...rest } = change;
            setLines((current) => current.map((line) => (line.key === target.key ? { ...line, ...rest, description: line.description || change.description || "" } : line)));
          } else {
            added++;
            const fresh: DraftLine = { ...blankLine(), isSlab: true, ...change, description: change.description ?? "" };
            claimed.add(fresh.key);
            setLines((current) => {
              const untouched = current.length === 1 && !current[0]!.productId && !current[0]!.isSlab && !current[0]!.description && !current[0]!.lineTotal && !current[0]!.newProduct;
              return untouched ? [fresh] : [...current, fresh];
            });
          }
          if (got.unsure.length) unsure.push(`${got.certNo ?? file.name}: ${unsureSentence(got.unsure, SLAB_WORDS) ?? ""}`);
        } catch (problem) {
          failed.push(`${file.name} (${problem instanceof ApiError ? problem.message : "could not be read"})`);
        }
        done++;
        setReadingMany({ at: done, of: photos.length });
      }
    };
    await Promise.all([worker(), worker()]);
    setReadingMany(undefined);
    setReadNotes([
      `Read ${filled + added} of ${photos.length} slab photos: ${filled} filled slab lines already on this purchase${added ? `, ${added} added as new slab lines. Put a price on the new ones` : ""}. Check every certificate number against its photo before saving.`,
      ...(failed.length ? [`Not read: ${failed.join(", ")}. Try those again, one by one, from the line.`] : []),
      ...unsure,
    ]);
  }

  async function readLabel(key: number, file: File): Promise<void> {
    setReadingLine(key);
    setError(undefined);
    try {
      const { got, change } = await slabFromPhoto(file);
      update(key, change);
      const unsure = unsureSentence(got.unsure, SLAB_WORDS);
      if (unsure) setReadNotes([unsure]);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not read the label");
    } finally {
      setReadingLine(undefined);
    }
  }

  const [pasting, setPasting] = useState(false);

  /** Rows pasted from a spreadsheet, added after the lines already there (an untouched first line is replaced). */
  function addPasted(rows: readonly PastedRow[]): void {
    const made: DraftLine[] = rows.map((row) => {
      const base = { ...blankLine(), quantity: String(row.quantity), lineTotal: row.total, unitPrice: priceForAmount(row.total, String(row.quantity)) };
      if (row.kind === "slabs") {
        return {
          ...base,
          isSlab: true,
          description: row.name,
          slab: { cardName: row.name.replace(/\s*\b(PSA|BGS|CGC|SGC|TAG|ACE)\s*\d+(\.\d)?\s*$/i, "").trim(), ...(row.grader ? { grader: row.grader, grade: row.grade ?? "" } : {}) },
        };
      }
      if (row.productId) return { ...base, productId: row.productId };
      return { ...base, newProduct: { itemType: row.itemType }, description: row.name };
    });
    setLines((current) => {
      const untouched = current.length === 1 && !current[0]!.productId && !current[0]!.isSlab && !current[0]!.description && !current[0]!.lineTotal && !current[0]!.newProduct;
      return untouched ? made : [...current, ...made];
    });
    setPasting(false);
  }

  function update(key: number, change: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  function step(line: DraftLine, by: number): void {
    const current = Number(line.quantity);
    const next = Math.max(1, (Number.isFinite(current) ? current : 1) + by);
    changeQuantity(line, String(next));
  }

  function changeQuantity(line: DraftLine, quantity: string): void {
    update(line.key, { quantity, lineTotal: amountForPrice(line.unitPrice, quantity) });
  }

  async function submit(event?: FormEvent): Promise<void> {
    event?.preventDefault();
    setError(undefined);
    const short = lines.findIndex((line) => (expenses || line.notForResale) && (!line.categoryId || !line.description.trim()));
    if (short >= 0) return setError(`Line ${short + 1} needs a category and a description`);
    const unnamed = lines.findIndex((line) => line.newProduct && !line.description.trim());
    if (unnamed >= 0) return setError(`Line ${unnamed + 1} is a new product: type its name`);
    setBusy(true);
    try {
      const net = spreadDiscount(lines.map((line) => toCents(line.lineTotal) ?? 0), discountCents);
      const input = {
        purchasedOn,
        supplierContactId: supplierId.startsWith("person:") || supplierId.startsWith("history:") ? null : supplierId || null,
        supplierPersonId: supplierId.startsWith("person:") ? supplierId.slice(7) : null,
        supplierName: supplierId.startsWith("history:") ? historicalSupplier ?? null : supplierId ? null : supplierName || null,
        paidFromAccountId: accountId,
        purchaseBatchRef: batchRef || null,
        supplierInvoiceNo: invoiceNo || null,
        ...(supplierDetails ? { supplierDetails } : {}),
        ...(discountCents > 0 ? { notes: `Invoice discount of S${formatMoney(discountCents)} spread over the lines (${formatMoney(gross)} before it)` } : {}),
        lines: lines.map((line, index) => ({
          productId: line.isSlab || line.categoryId || line.newProduct ? null : line.productId || null,
          isSlab: line.isSlab,
          ...(line.categoryId ? { categoryId: line.categoryId } : {}),
          ...(line.newProduct && !line.isSlab && !line.categoryId
            ? {
                newProduct: {
                  name: languageOf(line.description).rest,
                  language: languageOf(line.description).language,
                  itemType: line.newProduct.itemType,
                  category: categoryFor(line.newProduct.itemType),
                  section: typeOf(line.newProduct.itemType).section || null,
                },
              }
            : {}),
          description:
            line.description ||
            (line.isSlab ? "" : (products.find((product) => product.id === line.productId)?.name ?? "")),
          quantity: line.isSlab ? 1 : Number(line.quantity) || 1,
          lineTotal: discountCents > 0 ? ((net[index] ?? 0) / 100).toFixed(2) : line.lineTotal,
          // A slab keyed without its cert number is still stock, marked to finish later.
          isComplete: !line.isSlab,
          ...(line.isSlab && line.slab ? { slab: line.slab } : {}),
          // So the next invoice with these words picks this product by itself (D, 6 October 2026).
          ...(!line.isSlab && line.readAs && line.productId ? { invoiceWords: line.readAs } : {}),
        })),
      };
      const result = editing ? await api.replacePurchase(editing.id, input) : await api.addPurchase(input);
      if (!editing) clearDraft(mode);
      if (invoiceFile) {
        try {
          await api.attachToPurchase(result.purchase.id, invoiceFile.file);
        } catch {
          // The purchase is saved; the invoice can still be attached from the list.
        }
      }
      // Each graded card keeps the photo its label was read from, in line order.
      for (const [index, line] of lines.entries()) {
        const lotId = result.purchase.lines[index]?.lotId;
        if (line.isSlab && line.slabPhoto && lotId) await api.setSlabPhoto(lotId, line.slabPhoto).catch(() => undefined);
      }
      onSaved(result.purchase);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Could not save the purchase");
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      size="lg"
      title={editing ? `Edit ${editing.number}` : expenses ? "Record expense" : "Record inventory purchase"}
      onClose={onClose}
      footer={
        <>
          <span className="me-auto font-semibold text-[1rem]">Total {formatMoney(total)}</span>
          {editing ? null : (
            <button
              type="button"
              className="ti-btn ti-btn-light"
              disabled={busy}
              onClick={() => {
                const kept = saveDraft(mode, {
                  purchasedOn,
                  supplierId,
                  supplierName,
                  accountId,
                  batchRef,
                  invoiceNo,
                  discount,
                  ...(supplierDetails ? { supplierDetails } : {}),
                  // A graded card's photo is too large to keep here; it is read again on return.
                  lines: lines.map(({ slabPhoto: _photo, ...line }) => line),
                  ...(invoiceFile ? { invoiceFile } : {}),
                });
                onDraft(kept);
              }}
            >
              <i className="ri-save-line" /> Save as draft
            </button>
          )}
          <button type="button" className="ti-btn ti-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="ti-btn ti-btn-primary-full"
            disabled={busy || !accountId}
            onClick={() => void submit()}
          >
            {busy ? <Working label="Saving" /> : expenses ? "Save expense" : "Save purchase"}
          </button>
        </>
      }
    >
      <form onSubmit={(event) => void submit(event)}>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {canRead ? (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <ReadFileButton
              id="pu-read"
              label={expenses ? "Read invoice or receipt" : "Read supplier invoice"}
              accept="image/*,application/pdf"
              busy={reading}
              onFile={(file) => void readInvoiceFile(file)}
            />
            <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
              Upload a photo or PDF. The form is filled in for you to review before saving.
            </span>
          </div>
        ) : null}
        {readNotes.length > 0 ? (
          <Alert tone="info" onClose={() => setReadNotes([])}>
            <ul className="list-disc ms-4 mb-0">
              {readNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <div className="grid grid-cols-12 gap-4">
          <Field id="pu-date" label={expenses ? "Date" : "Purchase date"} className="md:col-span-4 col-span-12">
            <DateField id="pu-date" value={purchasedOn} onChange={setPurchasedOn} />
          </Field>
          <Field id="pu-supplier" label={expenses ? "Paid to" : "Supplier"} className="md:col-span-4 col-span-12">
            <SelectField
              id="pu-supplier"
              value={supplierId || "other"}
              options={[
                { value: "other", label: expenses ? "Not on the list" : "Supplier not listed" },
                ...suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })),
                ...historicalSuppliers.map((name) => ({ value: `history:${encodeURIComponent(name)}`, label: name })),
                ...supplierPeople.map((person) => ({ value: `person:${person.id}`, label: `${person.fullName} (staff)` })),
              ]}
              onChange={(choice) => setSupplierId(choice === "other" ? "" : choice)}
            />
          </Field>
          <Field id="pu-supplier-name" label={expenses ? "Name, if not on the list" : "Supplier name"} className="md:col-span-4 col-span-12">
            <input
              id="pu-supplier-name"
              type="text"
              className="form-control"
              value={supplierId ? (supplierId.startsWith("history:") ? historicalSupplier ?? "" : supplierId.startsWith("person:") ? supplierPeople.find((person) => person.id === supplierId.slice(7))?.fullName ?? "" : suppliers.find((s) => s.id === supplierId)?.name ?? "") : supplierName}
              disabled={Boolean(supplierId)}
              placeholder={expenses ? "Inches Interior Designer" : "Seller at the Suntec event"}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </Field>
          <Field id="pu-account" label="Paid from account" className="md:col-span-4 col-span-12">
            <SelectField
              id="pu-account"
              value={accountId}
              placeholder="Select account"
              options={accounts.map((entry) => ({
                value: entry.id,
                label: `${entry.ownerKind === "company" ? "Company" : entry.ownerName}: ${entry.name}`,
              }))}
              onChange={setAccountId}
            />
          </Field>
          {expenses ? null : (
          <Field id="pu-batch" label="Batch code" className="md:col-span-4 col-span-12">
            <input
              id="pu-batch"
              type="text"
              className="form-control"
              value={batchRef}
              placeholder="AHEN-20260705-001"
              onChange={(e) => setBatchRef(e.target.value.toUpperCase())}
            />
          </Field>
          )}
          <Field
            id="pu-invoice-file"
            label={expenses ? "Invoice or receipt" : "Supplier invoice"}
            className="col-span-12"
            hint={invoiceFile ? `${invoiceFile.name} is saved with the purchase.` : "A photo or the PDF. Saved with the purchase."}
          >
            <input
              id="pu-invoice-file"
              type="file"
              accept="image/*,application/pdf"
              className="form-control"
              onChange={(changed) => {
                const file = changed.target.files?.[0];
                if (!file) return setInvoiceFile(undefined);
                void prepareFile(file)
                  .then((prepared) => setInvoiceFile({ name: file.name, file: prepared }))
                  .catch(() => setError("That file could not be read"));
              }}
            />
          </Field>
          <Field id="pu-invoice" label={expenses ? "Invoice number" : "Supplier invoice number"} className="md:col-span-4 col-span-12">
            <input
              id="pu-invoice"
              type="text"
              className="form-control"
              value={invoiceNo}
              placeholder="Optional"
              onChange={(e) => setInvoiceNo(e.target.value)}
            />
          </Field>
        </div>

        {fromDraft ? (
          <div className="mt-4">
            <Alert tone="info">
              Carrying on from the draft saved {draft?.savedAt ? new Date(draft.savedAt).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "earlier"}.
              <button
                type="button"
                className="ti-btn ti-btn-light !py-1 !px-2 !text-[0.75rem] !mb-0 ms-2"
                onClick={() => {
                  clearDraft(mode);
                  setFromDraft(false);
                  setPurchasedOn(singaporeToday());
                  setSupplierId("");
                  setSupplierName("");
                  setBatchRef("");
                  setInvoiceNo("");
                  setDiscount("");
                  setSupplierDetails(undefined);
                  setInvoiceFile(undefined);
                  setLines([blankLine()]);
                }}
              >
                Discard the draft and start again
              </button>
            </Alert>
          </div>
        ) : null}
        {account ? (
          <div className="mt-4">
            <Alert tone={account.ownerKind === "company" ? "primary" : "warning"}>
              {expenses
                ? account.ownerKind === "company"
                  ? "A payment voucher is issued for it. Nothing goes into inventory."
                  : `Paid by ${account.ownerName} personally, so no payment voucher is issued. Nothing goes into inventory.`
                : account.ownerKind === "company"
                ? "This becomes company inventory, and a payment voucher is issued for it."
                : `This becomes inventory personally funded by ${account.ownerName}. No payment voucher is issued because the company has not paid anyone. It becomes company inventory when the company buys it from them.`}
            </Alert>
          </div>
        ) : null}

{supplierDetails ? (
          <div className="rounded-md border border-defaultborder bg-light dark:bg-bodybg px-3 py-2 mb-4 text-[0.813rem]">
            <span className="font-semibold">Supplier details from the invoice</span>, kept with the supplier on saving:
            <div className="text-[#8c9097] dark:text-white/50">
              {[
                supplierDetails.address,
                supplierDetails.phone,
                supplierDetails.email,
                supplierDetails.website,
                supplierDetails.uen ? `Reg. no. ${supplierDetails.uen}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        ) : null}
        {pasting ? <PasteList products={products} onClose={() => setPasting(false)} onAdd={addPasted} /> : null}
                <div className="table-responsive">
          <table className="table whitespace-nowrap border dark:border-defaultborder/10 mt-3 w-full [&_td]:!px-1.5 [&_th]:!px-1.5">
            <thead>
              <tr className="border-b border-defaultborder">
                <th scope="col" className="text-start">{expenses ? "Category" : "Product"}</th>
                <th scope="col" className="text-start">{expenses ? "Description" : "Graded card description"}</th>
                <th scope="col" className="text-start">Quantity</th>
                <th scope="col" className="text-start">Price S$</th>
                <th scope="col" className="text-start">Line total</th>
                <th scope="col" className="text-start">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={line.key} className="border-b border-defaultborder">
                  <td>
                    <SelectField
                      id={`pl-kind-${line.key}`}
                      label={`Item on line ${index + 1}`}
                      className="w-[11.5rem]"
                      value={line.categoryId ? `__cat:${line.categoryId}` : line.isSlab ? "__slab" : line.newProduct ? "__new" : line.productId}
                      placeholder={expenses || line.notForResale ? "Select category" : "Select"}
                      options={
                        // An expense is a category and nothing else; stock is a product or a graded card.
                        expenses || line.notForResale
                          ? categories.map((category) => ({ value: `__cat:${category.id}`, label: category.name }))
                          : [
                              { value: "__slab", label: "Graded card" },
                              { value: "__new", label: "New product, not on the list yet" },
                              ...products.map((product) => ({ value: product.id, label: `${productCode(product)} ${productName(product)}${product.game ? ` (${product.game})` : ""}` })),
                            ]
                      }
                      onChange={(choice) => {
                        const { categoryId: _was, newProduct: _new, ...rest } = line;
                        if (choice === "__new") {
                          setLines((current) => current.map((one) => (one.key === line.key ? { ...rest, isSlab: false, productId: "", newProduct: { itemType: "single" }, description: line.description || line.readAs || "" } : one)));
                        } else if (choice.startsWith("__cat:")) {
                          setLines((current) => current.map((one) => (one.key === line.key ? { ...rest, isSlab: false, productId: "", categoryId: choice.slice(6), description: line.description || line.readAs || "" } : one)));
                        } else if (choice === "__slab") {
                          setLines((current) => current.map((one) => (one.key === line.key ? { ...rest, isSlab: true, productId: "", quantity: "1", unitPrice: priceForAmount(line.lineTotal, "1"), description: line.readAs ?? "" } : one)));
                        } else {
                          setLines((current) => current.map((one) => (one.key === line.key ? { ...rest, isSlab: false, productId: choice, description: "" } : one)));
                        }
                      }}
                    />
                    
                    {line.notForResale && !expenses ? <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-1 mb-0">Not for resale: not put into inventory.</p> : null}
                    {line.readAs && !line.isSlab ? (
                      <p className="text-[0.75rem] text-info mt-1 mb-0 whitespace-normal break-words w-[11.5rem]">Invoice description: {line.readAs}</p>
                    ) : null}
                  </td>
                  <td>
                    <div className="w-[10.5rem] whitespace-normal">
                    <input
                      id={`pl-desc-${line.key}`}
                      aria-label={`Card on line ${index + 1}`}
                      type="text"
                      className="form-control field-grows"
                      value={line.description}
                      disabled={!line.isSlab && !line.categoryId && !expenses && !line.notForResale && !line.newProduct}
                      placeholder={line.isSlab ? "Charizard Holo PSA 10" : line.newProduct ? "Name, e.g. JP Promo Pikachu #144/S-P" : expenses ? "What it was, e.g. Renovation of the shop" : line.notForResale ? "What it was, e.g. Trolley" : ""}
                      onChange={(e) => update(line.key, { description: e.target.value })}
                    />
                    {line.newProduct ? (
                      <select
                        aria-label={`Type of the new product on line ${index + 1}`}
                        className="form-select !py-1 !text-[0.75rem] mt-1"
                        value={line.newProduct.itemType}
                        onChange={(event) => update(line.key, { newProduct: { itemType: event.target.value as ItemType } })}
                      >
                        {PRODUCT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            New {type.label.toLowerCase()}
                          </option>
                        ))}
                      </select>
                    ) : null}
                    {line.isSlab && line.description ? (
                      <p className="text-[0.75rem] mt-1 mb-0 break-words">{line.description}</p>
                    ) : null}
                    {line.isSlab ? <div className="mt-2 grid grid-cols-3 gap-2">
                      <Field id={`pl-cert-${line.key}`} label="Certificate number" className="col-span-3"><input id={`pl-cert-${line.key}`} className="form-control" value={line.slab?.certNo ?? ""} onChange={(event) => update(line.key, { slab: { ...line.slab, certNo: event.target.value } })} /></Field>
                      <Field id={`pl-grader-${line.key}`} label="Grader" className="col-span-2"><input id={`pl-grader-${line.key}`} className="form-control" value={line.slab?.grader ?? ""} onChange={(event) => update(line.key, { slab: { ...line.slab, grader: event.target.value } })} /></Field>
                      <Field id={`pl-grade-${line.key}`} label="Grade" className="col-span-1"><input id={`pl-grade-${line.key}`} className="form-control" value={line.slab?.grade ?? ""} onChange={(event) => update(line.key, { slab: { ...line.slab, grade: event.target.value } })} /></Field>
                      {(!line.slab?.certNo?.trim() || !line.slab?.grader?.trim() || !line.slab?.grade?.trim()) ? <p className="col-span-3 text-warning text-[0.75rem] mb-0">This card will remain incomplete. Add the missing certificate number, grader or grade above. The description alone does not fill these fields.</p> : null}
                    </div> : null}
                    {/* D, 6 October 2026: read the slab's label off a photo, as the buyback does. */}
                    {line.isSlab && canRead ? (
                      <div className="mt-2 flex items-center gap-2">
                        <ReadFileButton
                          id={`pl-read-${line.key}`}
                          label="Read the label from a photo"
                          accept="image/*"
                          busy={readingLine === line.key}
                          onFile={(file) => void readLabel(line.key, file)}
                        />
                        <span className="text-[0.75rem] text-[#8c9097]">Use a clear close-up of the label. Check every certificate digit before saving.</span>
                        {line.slab?.certNo ? (
                          <span className="text-[0.75rem] text-[#8c9097] dark:text-white/50">
                            Cert {line.slab.certNo}
                            {line.slab.grader ? `, ${line.slab.grader} ${line.slab.grade ?? ""}` : ""}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    </div>
                  </td>
                  <td>
                    <div className="input-group dark:border-defaultborder/10 rounded-md !flex-nowrap">
                      <button
                        type="button"
                        aria-label="One fewer"
                        className="ti-btn ti-btn-icon !bg-primary !text-white input-group-text !mb-0 !rounded-e-none"
                        disabled={line.isSlab}
                        onClick={() => step(line, -1)}
                      >
                        <i className="ri-subtract-line" />
                      </button>
                      <input
                        id={`pl-qty-${line.key}`}
                        aria-label={`Quantity on line ${index + 1}`}
                        type="text"
                        inputMode="numeric"
                        className="form-control field-tiny !p-1"
                        value={line.isSlab ? "1" : line.quantity}
                        disabled={line.isSlab}
                        onChange={(e) => changeQuantity(line, e.target.value)}
                      />
                      <button
                        type="button"
                        aria-label="One more"
                        className="ti-btn ti-btn-icon !bg-primary !text-white input-group-text !mb-0 !rounded-s-none"
                        disabled={line.isSlab}
                        onClick={() => step(line, 1)}
                      >
                        <i className="ri-add-line" />
                      </button>
                    </div>
                  </td>
                  <td>
                    <input aria-label={`Unit price on line ${index + 1}`} type="text" inputMode="decimal" className="form-control !w-[5.75rem]" placeholder="0.00" value={line.unitPrice}
                      onChange={(event) => update(line.key, { unitPrice: event.target.value, lineTotal: amountForPrice(event.target.value, line.quantity) })} />
                  </td>
                  <td>
                    <input
                      id={`pl-total-${line.key}`}
                      aria-label={`Line total on line ${index + 1}`}
                      type="text"
                      inputMode="decimal"
                      className="form-control !w-[5.75rem]"
                      value={line.lineTotal}
                      placeholder="0.00"
                      onChange={(e) => update(line.key, { lineTotal: e.target.value, unitPrice: priceForAmount(e.target.value, line.quantity) })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      aria-label={`Remove line ${index + 1}`}
                      className="ti-btn ti-btn-sm ti-btn-icon ti-btn-danger !mb-0"
                      disabled={lines.length === 1}
                      onClick={() => setLines((current) => current.filter((entry) => entry.key !== line.key))}
                    >
                      <i className="ri-delete-bin-5-line" />
                    </button>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={5} className="border-bottom-0">
                  <button
                    type="button"
                    className="ti-btn ti-btn-light !font-medium !mb-0"
                    onClick={() => setLines((current) => [...current, blankLine()])}
                  >
                    <i className="ri-add-line" /> Add line
                  </button>
                  {expenses ? null : (
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !font-medium !mb-0 ms-2"
                      title="Something bought on the same invoice that is not stock: it is paid for with the rest and kept out of inventory"
                      onClick={() => setLines((current) => [...current, { ...blankLine(), notForResale: true }])}
                    >
                      <i className="ri-add-line" /> Add line not for resale
                    </button>
                  )}
                  {/* Every line read from an invoice that matched nothing, made a new single at once (D, 27 October 2026: 46 singles from DenryuTCG). */}
                  {!expenses && lines.some((line) => line.readAs && !line.productId && !line.isSlab && !line.categoryId && !line.newProduct) ? (
                    <button
                      type="button"
                      className="ti-btn ti-btn-primary !font-medium !mb-0 ms-2"
                      title="Each line the invoice read that is not on the product list becomes a new single card, named as the invoice has it"
                      onClick={() =>
                        setLines((current) =>
                          current.map((line) =>
                            line.readAs && !line.productId && !line.isSlab && !line.categoryId && !line.newProduct
                              ? { ...line, newProduct: { itemType: "single" }, description: line.description || line.readAs }
                              : line,
                          ),
                        )
                      }
                    >
                      <i className="ri-add-box-line" /> Make unmatched lines new singles (
                      {lines.filter((line) => line.readAs && !line.productId && !line.isSlab && !line.categoryId && !line.newProduct).length})
                    </button>
                  ) : null}
                  {expenses ? null : (
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !font-medium !mb-0 ms-2"
                      title="Paste rows from a spreadsheet: singles, sealed products or slabs, each row a line"
                      onClick={() => setPasting(true)}
                    >
                      <i className="ri-clipboard-line" /> Paste a list
                    </button>
                  )}
                  {expenses || !canRead ? null : (
                    <label
                      htmlFor="pu-slab-photos"
                      title="Pick or drop the photos of many slabs. Each label is read and fills its slab line."
                      className={`ti-btn ti-btn-light !font-medium !mb-0 ms-2 cursor-pointer ${readingMany ? "opacity-60 pointer-events-none" : ""}`}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        void readSlabPhotos([...event.dataTransfer.files]);
                      }}
                    >
                      <i className={readingMany ? "ri-loader-4-line animate-spin" : "ri-camera-lens-line"} />{" "}
                      {readingMany ? `Reading slab ${Math.min(readingMany.at + 1, readingMany.of)} of ${readingMany.of}` : "Read slab photos"}
                      <input
                        id="pu-slab-photos"
                        type="file"
                        accept="image/*"
                        multiple
                        className="sr-only"
                        disabled={Boolean(readingMany)}
                        onChange={(event) => {
                          const files = [...(event.target.files ?? [])];
                          event.target.value = "";
                          void readSlabPhotos(files);
                        }}
                      />
                    </label>
                  )}
                  {expenses ? null : (
                    <button
                      type="button"
                      className="ti-btn ti-btn-light !font-medium !mb-0 ms-2"
                      title="Fetch the product list again, after adding a product on Inventory"
                      onClick={() => void api.products().then((result) => setProducts(result.products.filter((product) => product.isActive)))}
                    >
                      <i className="ri-refresh-line" /> Refresh products
                    </button>
                  )}
                </td>
              </tr>
              <tr>
                <td colSpan={99} className="!pt-0">
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    <label htmlFor="pu-discount" className="form-label !mb-0">
                      Discount on the invoice
                    </label>
                    <input
                      id="pu-discount"
                      type="text"
                      inputMode="decimal"
                      className="form-control !w-[8rem] text-end"
                      value={discount}
                      placeholder="0.00"
                      onChange={(event) => setDiscount(event.target.value)}
                    />
                  </div>
                  {discountCents > 0 ? (
                    <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 text-end mt-1 mb-0 whitespace-normal">
                      {formatMoney(gross)} less {formatMoney(discountCents)} is {formatMoney(total)}, what the supplier is paid. The discount is
                      spread over the lines on saving, so each product's cost is what it really cost.
                    </p>
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

/** A discount off the whole, taken off each line in proportion, to the cent, adding up exactly. */
function spreadDiscount(lines: readonly number[], discount: number): number[] {
  const gross = lines.reduce((sum, line) => sum + line, 0);
  if (discount <= 0 || gross <= 0) return [...lines];
  const shares = lines.map((line) => Math.floor((line * discount) / gross));
  let left = discount - shares.reduce((sum, share) => sum + share, 0);
  // The odd cents go to the largest lines first.
  const order = lines.map((line, index) => ({ line, index })).sort((a, b) => b.line - a.line);
  for (const { index } of order) {
    if (left <= 0) break;
    if ((lines[index] ?? 0) > (shares[index] ?? 0)) {
      shares[index] = (shares[index] ?? 0) + 1;
      left -= 1;
    }
  }
  return lines.map((line, index) => line - (shares[index] ?? 0));
}

/** A purchase or expense form put down half done, kept in this browser. */
interface PurchaseDraft {
  readonly savedAt?: string;
  readonly purchasedOn: string;
  readonly supplierId: string;
  readonly supplierName: string;
  readonly accountId: string;
  readonly batchRef: string;
  readonly invoiceNo: string;
  readonly discount: string;
  readonly supplierDetails?: SupplierDetails;
  readonly lines: DraftLine[];
  readonly invoiceFile?: { name: string; file: PickedFile };
}

const draftKey = (mode: "inventory" | "expenses"): string => `ohc.purchase.draft.${mode}`;

function readDraft(mode: "inventory" | "expenses"): PurchaseDraft | undefined {
  try {
    const text = window.localStorage.getItem(draftKey(mode));
    return text ? (JSON.parse(text) as PurchaseDraft) : undefined;
  } catch {
    return undefined;
  }
}

/** Keeps the form. A browser holds only so much, so a large invoice file is left out and said so. */
function saveDraft(mode: "inventory" | "expenses", draft: PurchaseDraft): "all" | "without the file" {
  const stamped = { ...draft, savedAt: new Date().toISOString() };
  try {
    window.localStorage.setItem(draftKey(mode), JSON.stringify(stamped));
    return "all";
  } catch {
    const { invoiceFile: _file, ...rest } = stamped;
    try {
      window.localStorage.setItem(draftKey(mode), JSON.stringify(rest));
    } catch {
      // Nothing can be kept here at all, a private window say; the form closes as asked.
    }
    return "without the file";
  }
}

function clearDraft(mode: "inventory" | "expenses"): void {
  try {
    window.localStorage.removeItem(draftKey(mode));
  } catch {
    // Nothing was kept.
  }
}
